import React from 'react';
import { ArrowRight, Play, Sparkles, ShieldCheck } from 'lucide-react';
import { heroData } from '../mock';
import { useI18n } from '../context/I18nContext';
import ParticleField from './ParticleField';

export default function Hero() {
  const { tr } = useI18n();
  return (
    <section className="relative min-h-[100vh] flex items-center overflow-hidden pt-24">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={heroData.bgImage}
          alt="Aegis Studios Legendary Metin2"
          className="w-full h-full object-cover object-center scale-110 opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080b]/85 via-[#08080b]/55 to-[#08080b]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(228,180,78,0.18),transparent_55%)]" />

        {/* Animated grid */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(228,180,78,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(228,180,78,0.4) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
          }}
        />

        <ParticleField />
      </div>

      {/* Floating decorations */}
      <div className="absolute top-32 left-8 w-64 h-64 rounded-full bg-[#e4b44e]/10 blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
      <div className="absolute bottom-16 right-8 w-96 h-96 rounded-full bg-[#e4b44e]/8 blur-3xl animate-pulse" style={{ animationDuration: '7s' }} />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 w-full">
        <div className="grid lg:grid-cols-[1fr_auto] gap-12 items-center">
          <div className="max-w-3xl reveal">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#e4b44e]/35 bg-[#e4b44e]/8 mb-8 backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#f4d896] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#f4d896]" />
              </span>
              <Sparkles className="w-3.5 h-3.5 text-[#f4d896]" />
              <span className="text-[11px] font-bold tracking-[0.28em] text-[#f4d896]">{tr('home.hero_badge', heroData.eyebrow)}</span>
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[7.5rem] font-extrabold leading-[0.98] mb-8">
              <span className="text-white block">{tr('home.hero_title_1', heroData.title)}</span>
              <span className="gold-text block">{tr('home.hero_title_2', heroData.titleAccent)}</span>
            </h1>

            <p className="text-lg lg:text-xl text-white/70 max-w-2xl leading-relaxed mb-10">
              {tr('home.hero_desc', heroData.subtitle)}
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <a href={heroData.primaryCta.href} className="btn-gold group inline-flex items-center gap-2 h-14 px-8 rounded-full text-sm font-extrabold tracking-widest">
                {tr('home.cta_explore', heroData.primaryCta.label)}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </a>
              <a href={heroData.secondaryCta.href} className="btn-outline-gold inline-flex items-center gap-2 h-14 px-8 rounded-full text-sm font-bold tracking-widest">
                <Play className="w-4 h-4" />
                {tr('home.cta_popular', heroData.secondaryCta.label)}
              </a>
            </div>

            <div className="mt-14 grid grid-cols-3 max-w-lg gap-6">
              {[
                { k: '500+', v: tr('hero.happy', 'Mutlu Müşteri') },
                { k: '50+', v: tr('hero.custom', 'Özel Sistem') },
                { k: '99.9%', v: tr('hero.uptime', 'Uptime') },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-3xl lg:text-4xl font-black gold-text">{s.k}</div>
                  <div className="text-xs uppercase tracking-widest text-white/50 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Floating shield emblem - desktop only */}
          <div className="hidden lg:block relative w-[380px] h-[380px] reveal" style={{ animationDelay: '0.3s' }}>
            {/* Outer rotating ring */}
            <div className="absolute inset-0 rounded-full border border-[#e4b44e]/25" style={{ animation: 'spin 30s linear infinite' }}>
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#e4b44e]/40" style={{ transform: `rotate(${i * 30}deg) translateY(-190px)` }} />
              ))}
            </div>
            {/* Middle ring */}
            <div className="absolute inset-10 rounded-full border border-[#e4b44e]/20" style={{ animation: 'spin 45s linear infinite reverse' }} />
            {/* Inner glow */}
            <div className="absolute inset-16 rounded-full bg-gradient-to-br from-[#e4b44e]/25 via-[#e4b44e]/8 to-transparent blur-2xl" />
            {/* Center shield */}
            <div className="absolute inset-24 rounded-full bg-gradient-to-br from-[#1a1710] to-[#08080b] border border-[#e4b44e]/40 flex items-center justify-center shadow-[0_0_60px_-10px_rgba(228,180,78,0.5)]">
              <div className="absolute inset-3 rounded-full border border-[#e4b44e]/25" />
              <ShieldCheck className="w-24 h-24 text-[#f4d896]" strokeWidth={1.4} />
            </div>
            {/* Floating badges */}
            <div className="absolute -top-2 right-8 px-3 py-1.5 rounded-full bg-[#0f0e12] border border-[#e4b44e]/40 text-[10px] font-bold tracking-widest text-[#f4d896] animate-pulse">C++ ENGINE</div>
            <div className="absolute bottom-4 -left-4 px-3 py-1.5 rounded-full bg-[#0f0e12] border border-[#e4b44e]/40 text-[10px] font-bold tracking-widest text-[#f4d896] animate-pulse" style={{ animationDelay: '1s' }}>DDoS SHIELD</div>
            <div className="absolute top-1/2 -right-6 px-3 py-1.5 rounded-full bg-[#0f0e12] border border-[#e4b44e]/40 text-[10px] font-bold tracking-widest text-[#f4d896] animate-pulse" style={{ animationDelay: '0.5s' }}>60+ FPS</div>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#e4b44e]/70">
        <div className="h-10 w-[1px] bg-gradient-to-b from-[#e4b44e]/60 to-transparent" />
        <span className="text-[10px] tracking-[0.4em]">SCROLL</span>
      </div>
    </section>
  );
}
